import { defineComponent, computed, ref } from 'vue';
import {
  PaymentMethodExceptionLimit,
  PaymentMethodExceptionLimitCreateModel,
  PlayerTag,
} from '@/models/ps-management/exception-limit.model';
import { Delete } from '@element-plus/icons-vue';
import { limitExceptionSettingsFormRules } from '@/hooks/ps-management/limit-ecxeption-validation-rules';
import { storeToRefs } from 'pinia';
import { useTagListStore } from '@/store/dictionary/tag-list.store';
import AutocompleteSelect from '@/components/autocomplete-select/autocomplete-select.vue';
import { usePaymentMethodsExceptionLimitStore } from '@/store/ps-management/geo-payment-method-exception-limits.store';
import { usePermissionsStore } from '@/store/permissions.store';
import { PS_MANAGEMENT_PERMISSIONS } from '@/models/app-internal/permissions.model';
import { AutocompleteTagListSearchResult } from '@/models/dictionary/tag-list.model';
import { FormInstance } from 'element-plus';

export default defineComponent({
  name: 'LimitException',
  components: {
    AutocompleteSelect,
  },
  props: {
    exceptionLimitItem: {
      type: Object as () => PaymentMethodExceptionLimit,
      required: true,
    },
  },
  emits: ['update-exception-limit-item'],
  setup(props, { emit }) {
    const icons = { Delete };

    const LimitExceptionFormRef = ref<FormInstance>();

    const paymentMethodsExceptionLimitStore = usePaymentMethodsExceptionLimitStore();
    const { tagsByCurrency } = storeToRefs(paymentMethodsExceptionLimitStore);
    const {
      addGeoPaymentMethodExceptionLimit,
      deleteGeoPaymentMethodExceptionLimit,
      updateGeoPaymentMethodExceptionLimit,
    } = paymentMethodsExceptionLimitStore;

    const tagListStore = useTagListStore();
    const { autocompleteTagListSearchResult, isTagListLoading } = storeToRefs(tagListStore);
    const { clearTagList, tagListAutocompleteSearch, clearAutocompleteTagListSearchResult } = tagListStore;

    const { psManagementPermissions } = storeToRefs(usePermissionsStore());

    const handleSaveLimitException = (exceptionLimitId: string): void => {
      LimitExceptionFormRef.value?.validate((valid) => {
        if (valid) {
          saveLimitsException(exceptionLimitId);
        }
      });
    };

    const saveLimitsException = (exceptionLimitId?: string): void => {
      const exceptionLimitData: PaymentMethodExceptionLimitCreateModel = {
        ...props.exceptionLimitItem,
        min: Number(props.exceptionLimitItem.min),
        max: Number(props.exceptionLimitItem.max),
        playerTags: (props.exceptionLimitItem.playerTags).map(tag => tag.id),
      };

      if (exceptionLimitId) {
        updateGeoPaymentMethodExceptionLimit({
          exceptionLimitId,
          paymentMethodExceptionData: exceptionLimitData,
        });

        return;
      }

      addGeoPaymentMethodExceptionLimit(exceptionLimitData);
    };

    const getSearchTagList = (query: string): void => {
      tagListAutocompleteSearch({
        name: query,
        alreadySelectedTagList: props.exceptionLimitItem.playerTags,
      });
    };

    const handleUpdateField = (field: 'min' | 'max', value: string) => {
      emit('update-exception-limit-item', {
        ...props.exceptionLimitItem,
        [field]: Number(value),
      });
    };

    const selectedTags = computed<PlayerTag[]>(() =>
      tagsByCurrency.value[props.exceptionLimitItem.currency] || [],
    );

    const tagOptionListByCurrency = computed<AutocompleteTagListSearchResult[]>(() =>
      autocompleteTagListSearchResult.value.filter(
        tag => !selectedTags.value.some(selectedTag => selectedTag.id === tag.value.tagId),
      ),
    );

    const isDeleteLimitExceptionButtonAvailable = computed<boolean>(() =>
      Boolean(props.exceptionLimitItem.id) &&
      psManagementPermissions.value[PS_MANAGEMENT_PERMISSIONS.DELETE_METHOD_EXTENDED_LIMIT],
    );

    const isUpdateLimitExceptionButtonAllowed = computed<boolean>(() =>
      psManagementPermissions.value[PS_MANAGEMENT_PERMISSIONS.UPDATE_METHOD_EXTENDED_LIMIT],
    );

    return {
      icons,
      limitExceptionSettingsFormRules,
      LimitExceptionFormRef,
      saveLimitsException,
      isTagListLoading,
      clearTagList,
      tagListAutocompleteSearch,
      clearAutocompleteTagListSearchResult,
      autocompleteTagListSearchResult,
      getSearchTagList,
      tagOptionListByCurrency,
      isDeleteLimitExceptionButtonAvailable,
      isUpdateLimitExceptionButtonAllowed,
      handleSaveLimitException,
      deleteGeoPaymentMethodExceptionLimit,
      handleUpdateField,
    };
  },
});
